/**
 * useInterviewRecording — Isolated candidate webcam+mic recording hook.
 *
 * DESIGN PRINCIPLES
 * ─────────────────
 * • Reuses the MediaStream the interview page already owns (streamRef) —
 *   never calls getUserMedia itself, never opens a second camera/mic stream.
 * • Chunked, resilient upload: MediaRecorder timeslices are buffered until
 *   they reach the S3 multipart minimum part size, then uploaded directly to
 *   S3 via a presigned URL (never proxied through the backend).
 * • Recording is entirely best-effort: every failure is caught and logged.
 *   This hook must NEVER throw into the interview flow.
 * • Zero knowledge of scoring, proctoring, or random-capture modules.
 *
 * USAGE
 * ─────
 * const recordingControls = useInterviewRecording({
 *   interviewId,    // string — same id already passed to useRandomCapture
 *   streamRef,      // React ref holding the getUserMedia MediaStream (video+audio)
 *   isActive,       // boolean — true while interview is in questioning stage
 *   consentGiven,   // boolean — true once the candidate accepted the guidelines modal
 *   apiBaseUrl,     // string  — e.g. API_BASE_URL
 * });
 * // recordingControls.finalize() — call on submit/terminate
 * // recordingControls.markIncomplete() — best-effort call on beforeunload
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../context/AuthContext';
import API_BASE_URL from '../Config/config';

// ─── Constants ───────────────────────────────────────────────────────────────

const CHUNK_TIMESLICE_MS  = 5000;               // MediaRecorder flush interval
const MIN_PART_SIZE_BYTES = 5 * 1024 * 1024;    // 5 MB — S3 multipart minimum (except last part)
const MAX_RETRY_ATTEMPTS  = 3;
const RETRY_BASE_DELAY_MS = 1000;
const UPLOAD_TIMEOUT_MS   = 30000;
const FINALIZE_WAIT_TIMEOUT_MS = 8000; // max time to wait for in-flight uploads before finalizing anyway

// The interview page flips isActive to true (setInterviewStarted/setStage)
// BEFORE its own startStream() resolves, so streamRef.current can briefly be
// null at the moment this hook first checks it. Retry instead of giving up.
const STREAM_WAIT_INTERVAL_MS = 500;
const MAX_STREAM_WAIT_ATTEMPTS = 20; // ~10s total wait for the camera/mic stream to attach

const CANDIDATE_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function pickSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
  return CANDIDATE_MIME_TYPES.find(t => MediaRecorder.isTypeSupported(t)) || null;
}

async function uploadBlobToS3(presignedUrl, blob) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    const res = await fetch(presignedUrl, { method: 'PUT', body: blob, signal: controller.signal });
    if (!res.ok) return { ok: false, reason: `HTTP ${res.status}` };
    const etag = res.headers.get('ETag') || res.headers.get('etag');
    return { ok: true, etag };
  } catch (err) {
    return { ok: false, reason: err.name === 'AbortError' ? 'timeout' : (err.message || 'network_error') };
  } finally {
    clearTimeout(timer);
  }
}

export default function useInterviewRecording({ interviewId, streamRef, isActive, consentGiven = true }) {
  const [status, setStatus] = useState('idle'); // idle|recording|finalizing|completed|failed|unsupported|no_stream

  const recordingIdRef   = useRef(null);
  const recorderRef      = useRef(null);
  const startedRef       = useRef(false);
  const finalizedRef     = useRef(false);
  const isMountedRef     = useRef(true);

  const bufferChunksRef  = useRef([]);
  const bufferSizeRef    = useRef(0);
  const nextPartNumberRef = useRef(1);
  const inFlightUploadsRef = useRef([]); // array of Promises currently uploading a part

  const streamWaitAttemptsRef = useRef(0);
  const streamWaitTimeoutRef  = useRef(null);

  const clearStreamWait = () => {
    if (streamWaitTimeoutRef.current) {
      clearTimeout(streamWaitTimeoutRef.current);
      streamWaitTimeoutRef.current = null;
    }
  };

  const interviewIdRef = useRef(interviewId);
  useEffect(() => { interviewIdRef.current = interviewId; }, [interviewId]);

  // ── Part upload (with retry) ───────────────────────────────────────────────

  const uploadPart = useCallback(async (blob, partNumber) => {
    let lastReason = 'unknown';
    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        const recordingId = recordingIdRef.current;
        if (!recordingId) return;

        const urlRes = await api.post(`/interview-recordings/${recordingId}/parts/${partNumber}/url`, {}, { timeout: 8000 });
        const url = urlRes?.data?.data?.url;
        if (!url) { lastReason = 'no_presigned_url'; throw new Error(lastReason); }

        const { ok, etag, reason } = await uploadBlobToS3(url, blob);
        if (!ok) { lastReason = reason; throw new Error(lastReason); }

        await api.post(`/interview-recordings/${recordingId}/parts/${partNumber}/confirm`, {
          etag: etag || `part-${partNumber}`,
          size: blob.size,
        }, { timeout: 8000 });

        console.log(`[InterviewRecording] chunk upload succeeded recordingId=${recordingId} partNumber=${partNumber}`);
        return;
      } catch (err) {
        lastReason = err.message || 'upload_error';
        console.warn(`[InterviewRecording] chunk upload attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed partNumber=${partNumber}: ${lastReason}`);
        if (attempt < MAX_RETRY_ATTEMPTS) {
          const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    console.error(`[InterviewRecording] chunk upload permanently failed partNumber=${partNumber} reason=${lastReason}`);
  }, []);

  const flushBuffer = useCallback((force = false) => {
    if (!force && bufferSizeRef.current < MIN_PART_SIZE_BYTES) return;
    if (bufferChunksRef.current.length === 0) return;

    const chunks = bufferChunksRef.current;
    bufferChunksRef.current = [];
    bufferSizeRef.current = 0;

    const partNumber = nextPartNumberRef.current++;
    const blob = new Blob(chunks, { type: chunks[0]?.type || 'video/webm' });
    console.log(`[InterviewRecording] chunk upload queued partNumber=${partNumber} size=${blob.size}`);
    const uploadPromise = uploadPart(blob, partNumber).catch(() => {});
    inFlightUploadsRef.current.push(uploadPromise);
  }, [uploadPart]);

  // ── Start recording ─────────────────────────────────────────────────────────

  const start = useCallback(async () => {
    if (startedRef.current) return;
    if (!consentGiven) return;
    if (typeof MediaRecorder === 'undefined') {
      console.warn('[InterviewRecording] MediaRecorder not supported in this browser');
      setStatus('unsupported');
      return;
    }

    const stream = streamRef?.current;
    if (!stream || typeof stream.getTracks !== 'function' || stream.getTracks().length === 0) {
      // The interview page sets isActive=true before its own startStream()
      // resolves — wait for the stream to attach instead of giving up.
      if (streamWaitAttemptsRef.current < MAX_STREAM_WAIT_ATTEMPTS) {
        streamWaitAttemptsRef.current += 1;
        console.log(`[InterviewRecording] stream not ready yet, retrying (${streamWaitAttemptsRef.current}/${MAX_STREAM_WAIT_ATTEMPTS})`);
        streamWaitTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) start();
        }, STREAM_WAIT_INTERVAL_MS);
      } else {
        console.warn('[InterviewRecording] No active media stream to record after waiting ~10s');
        setStatus('no_stream');
      }
      return;
    }
    console.log('[InterviewRecording] stream found — proceeding to initialize recording');

    const mimeType = pickSupportedMimeType();
    if (!mimeType) {
      console.warn('[InterviewRecording] No supported recording mime type');
      setStatus('unsupported');
      return;
    }

    try {
      console.log(`[InterviewRecording] init started interviewId=${interviewIdRef.current}`);
      const initRes = await api.post('/interview-recordings/init', { interviewId: interviewIdRef.current, mimeType }, { timeout: 10000 });
      const recordingId = initRes?.data?.data?.recordingId;
      if (!recordingId) throw new Error('init did not return a recordingId');
      recordingIdRef.current = recordingId;
      console.log(`[InterviewRecording] init succeeded recordingId=${recordingId}`);

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          bufferChunksRef.current.push(e.data);
          bufferSizeRef.current += e.data.size;
          flushBuffer(false);
        }
      };
      recorder.onerror = (e) => {
        console.error('[InterviewRecording] MediaRecorder error:', e?.error?.message || e);
      };
      recorder.start(CHUNK_TIMESLICE_MS);
      recorderRef.current = recorder;
      startedRef.current = true;
      setStatus('recording');
      console.log(`[InterviewRecording] recorder started recordingId=${recordingId} interviewId=${interviewIdRef.current}`);
    } catch (err) {
      console.error(`[InterviewRecording] init failed (interview continues normally): ${err.message}`);
      setStatus('failed');
    }
  }, [consentGiven, streamRef, flushBuffer]);

  // ── Finalize (normal submit/terminate path) ─────────────────────────────────

  const finalize = useCallback(async ({ durationSeconds } = {}) => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    clearStreamWait(); // don't let a delayed stream-wait retry call start() after finalize

    if (!startedRef.current || !recordingIdRef.current) {
      console.log('[InterviewRecording] finalize skipped — recording was never started for this session');
      return;
    }
    console.log(`[InterviewRecording] finalize started recordingId=${recordingIdRef.current}`);
    setStatus('finalizing');

    try {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        await new Promise((resolve) => {
          recorder.onstop = resolve;
          try { recorder.stop(); } catch { resolve(); }
          setTimeout(resolve, 3000); // safety net
        });
      }

      flushBuffer(true); // upload whatever remains, even if under the min part size

      await Promise.race([
        Promise.all(inFlightUploadsRef.current),
        new Promise(resolve => setTimeout(resolve, FINALIZE_WAIT_TIMEOUT_MS)),
      ]);

      const res = await api.post(`/interview-recordings/${recordingIdRef.current}/finalize`, { durationSeconds }, { timeout: 15000 });
      const finalStatus = res?.data?.data?.status || 'COMPLETED';
      setStatus(finalStatus.toLowerCase());
      console.log(`[InterviewRecording] finalize succeeded recordingId=${recordingIdRef.current} status=${finalStatus}`);
    } catch (err) {
      console.error(`[InterviewRecording] finalize failed (interview submission is unaffected): ${err.message}`);
      setStatus('failed');
    }
  }, [flushBuffer]);

  // ── Best-effort mark-incomplete (beforeunload/disconnect path) ──────────────

  const markIncomplete = useCallback(() => {
    clearStreamWait();
    if (finalizedRef.current || !startedRef.current || !recordingIdRef.current) return;
    finalizedRef.current = true;
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL.replace(/\/$/, '')}/interview-recordings/${recordingIdRef.current}/mark-incomplete`;
      fetch(url, {
        method: 'POST',
        body: new Blob([JSON.stringify({})], { type: 'application/json' }),
        keepalive: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => {});
      console.log(`[InterviewRecording] recording marked incomplete on unload recordingId=${recordingIdRef.current}`);
    } catch { /* best-effort only */ }
  }, []);

  // ── Lifecycle: start/stop tied to isActive ──────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;
    if (isActive && consentGiven) {
      console.log('[InterviewRecording] start detected — interview active and consent given');
      start();
    }
    return () => { isMountedRef.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, consentGiven]);

  useEffect(() => {
    return () => {
      clearStreamWait();
      // Component unmount: stop the recorder engine only (never the underlying
      // camera/mic tracks — those belong to the interview page).
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* ignore */ }
      }
    };
  }, []);

  return { status, finalize, markIncomplete };
}
